/**
 * /security — landing page for the Security section.
 *
 * Two-column overview: admin count + link to /security/admins, role
 * count + link to /security/roles, plus a link to /security/permissions.
 * Gating: parent route uses RequirePermission permission="admin.list".
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §6.6.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { Shield, Users, Key, Lock } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';

interface AdminRow {
  id: string;
  is_active: boolean;
}
interface RoleRow {
  id: string;
  key: string;
  member_count: number;
}

export function SecurityPage() {
  const apiUrl = useApiUrl();
  const [adminTotal, setAdminTotal] = useState<number | null>(null);
  const [adminActive, setAdminActive] = useState<number | null>(null);
  const [roles, setRoles] = useState<RoleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [admins, rolesList] = await Promise.all([
          adminFetch(`${apiUrl}/v1/admin/admins`).then(r => r.json() as Promise<AdminRow[]>),
          adminFetch(`${apiUrl}/v1/admin/roles`).then(r => r.json() as Promise<RoleRow[]>),
        ]);
        if (cancelled) return;
        setAdminTotal(admins.length);
        setAdminActive(admins.filter(a => a.is_active).length);
        setRoles(rolesList);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load security overview');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Security</h2>
        <p className="text-muted-foreground">
          Admin users, roles, and permissions
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Link
          to="/security/admins"
          className="block rounded-lg border bg-card p-6 transition-colors hover:bg-muted/50"
        >
          <div className="mb-3 flex items-center gap-3">
            <Users className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-semibold">Admin users</h3>
          </div>
          <p className="text-3xl font-bold">
            {adminTotal === null ? '—' : adminTotal}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {adminActive === null
              ? 'Loading admin accounts…'
              : `${adminActive} active`}
          </p>
          <p className="mt-4 text-sm font-medium text-primary">
            Manage admins →
          </p>
        </Link>

        <Link
          to="/security/roles"
          className="block rounded-lg border bg-card p-6 transition-colors hover:bg-muted/50"
        >
          <div className="mb-3 flex items-center gap-3">
            <Key className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-semibold">Roles</h3>
          </div>
          <p className="text-3xl font-bold">
            {roles === null ? '—' : roles.length}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">roles defined</p>
          {roles && (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {roles.map(r => (
                <li key={r.id}>
                  <code>{r.key}</code> — {r.member_count} member
                  {r.member_count === 1 ? '' : 's'}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-sm font-medium text-primary">
            Manage roles →
          </p>
        </Link>
      </div>

      <Link
        to="/security/permissions"
        className="block rounded-lg border bg-card p-6 transition-colors hover:bg-muted/50"
      >
        <div className="mb-2 flex items-center gap-3">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-base font-semibold">Permission catalog</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Browse every permission grouped by namespace, see which are
          destructive, and which roles grant each one.
        </p>
        <p className="mt-3 text-sm font-medium text-primary">
          View permissions →
        </p>
      </Link>

      <div className="rounded-lg border bg-card p-6">
        <div className="mb-3 flex items-center gap-3">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-base font-semibold">About this section</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Manage who has access to the admin panel and what they can do.
          Every action (login, role change, deactivation) is recorded in the
          admin audit log. Only Super Admins can grant or revoke
          super_admin; you can't deactivate yourself or the last Super
          Admin.
        </p>
      </div>
    </div>
  );
}
